import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as Logo } from '../assets/logo.svg';
import { GlobalContext } from '../App';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config';
import LoginModal from './LoginModal';
import Modal from './Modal';
import CreateIdeaModal from './CreateIdeaModal';
import CreateContentModal from './CreateContentModal';
import CreateArticleModal from './CreateArticleModal';
import SearchModal from './SearchModal';
import { SearchIcon, PenIcon, UserIcon, LogoutIcon, MenuIcon } from './Icons';
import '../styles/components/navbar.css';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [createIdeaModalOpen, setCreateIdeaModalOpen] = useState(false);
  const [createContentModalOpen, setCreateContentModalOpen] = useState(false);
  const [createArticleModalOpen, setCreateArticleModalOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [initialSearchQuery, setInitialSearchQuery] = useState('');
  const globalConfig = useContext(GlobalContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  
  useEffect(() => {
    if (globalConfig) {
      //console.log('Navbar received global config:', globalConfig);
      //console.log('Header config:', globalConfig.Header);
    }
  }, [globalConfig]);
  
  // 检查URL参数中的搜索查询
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('search');
    
    if (searchParam) {
      setInitialSearchQuery(searchParam);
      setSearchModalOpen(true);
      
      // 清除URL中的搜索参数，避免重复触发
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('search');
      window.history.replaceState({}, '', newUrl.pathname + newUrl.search);
    }
  }, [location.search]);
  
  // 监听自定义搜索事件
  useEffect(() => {
    const handleOpenSearchModal = (event) => {
      const { query } = event.detail;
      setInitialSearchQuery(query);
      setSearchModalOpen(true);
    };

    window.addEventListener('openSearchModal', handleOpenSearchModal);
    
    return () => {
      window.removeEventListener('openSearchModal', handleOpenSearchModal);
    };
  }, []);
  


  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle clicks outside the dropdown menu and mobile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && 
          !event.target.closest('.mobile-menu-toggle')) {
        setMobileMenuOpen(false);
      }
    };

    if (dropdownOpen || mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, mobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const handleLogout = () => {
    logout();
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    navigate('/');
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleLoginModal = () => {
    setLoginModalOpen(!loginModalOpen);
    setMobileMenuOpen(false);
  };
  
  const toggleSearchModal = () => {
    setSearchModalOpen(!searchModalOpen);
    setMobileMenuOpen(false);
  };

  const toggleCreateContentModal = () => {
    setCreateContentModalOpen(!createContentModalOpen);
    setMobileMenuOpen(false);
  };
  
  const toggleCreateArticleModal = () => {
    setCreateArticleModalOpen(!createArticleModalOpen);
    setMobileMenuOpen(false);
  };
  
  const handleCreateIdea = () => {
    setCreateContentModalOpen(false);
    setCreateIdeaModalOpen(true);
  };
  
  const handleCreateArticle = () => {
    setCreateContentModalOpen(false);
    setCreateArticleModalOpen(true);
  };

  const handleCreateIdeaSubmit = async () => {
    // 关闭创建片语模态框
    setCreateIdeaModalOpen(false);
    
    // 这里不需要额外刷新数据，因为SSE应该会自动处理
    // 但为了确保，我们可以触发一个自定义事件，通知IdeasPage组件刷新数据
    const refreshEvent = new CustomEvent('refreshIdeas');
    window.dispatchEvent(refreshEvent);
  };

  const handleMobileNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <nav className={`nav ${scrolled ? 'scrolled' : ''}`} id="nav">
        <div className="nav-container">
          <Link to="/" className="logo">
            {globalConfig?.Header?.logo?.image?.url ? (
              <img
                src={globalConfig.Header.logo.image.url.startsWith('http')
                  ? globalConfig.Header.logo.image.url
                  : `${API_URL}${globalConfig.Header.logo.image.url}`
                }
                alt={globalConfig?.siteName || 'Logo'}
                className="logo-image"
              />
            ) : (
              <Logo className="logo-icon" />
            )}
            <span className="logo-text">{globalConfig?.siteName || 'Blogname'}</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="nav-menu desktop-only">
            {globalConfig?.Header?.navitems?.map((item, index) => (
              <NavLink 
                key={index} 
                to={item.herf || '/'} 
                className="nav-link"
                end={item.herf === '/'}
              >
                {item.label}
              </NavLink>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="nav-actions desktop-only">
            <button className="nav-icon-button touch-target" title="搜索" onClick={toggleSearchModal}>
              <SearchIcon size={20} />
            </button>
            {isAuthenticated ? (
              <>
                <button 
                  className="nav-icon-button touch-target"
                  onClick={toggleCreateContentModal}
                  title="创建内容"
                >
                  <PenIcon size={20} />
                </button>
                <div className="user-menu" ref={dropdownRef}>
                  <div className="user-info touch-target" onClick={toggleDropdown}>
                    {user.avatar ? (
                      <img
                        src={user.avatar.url.startsWith('http')
                          ? user.avatar.url
                          : `${API_URL}${user.avatar.url}`
                        }
                        alt={user.username}
                        className="user-avatar"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.parentNode.innerHTML = `<div class="user-avatar-placeholder">${user.username.charAt(0).toUpperCase()}</div>`;
                        }}
                      />
                    ) : (
                      <div className="user-avatar-placeholder">
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  {dropdownOpen && (
                    <div className="dropdown-menu">
                      <div className="dropdown-header">
                        <span className="username">{user.username}</span>
                      </div>
                      <Link to="/profile" className="dropdown-item" onClick={() => setDropdownOpen(false)}>
                        <UserIcon size={16} />
                        个人资料
                      </Link>
                      <button className="dropdown-item logout-button" onClick={handleLogout}>
                        <LogoutIcon size={16} />
                        退出登录
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <button className="nav-icon-button touch-target" onClick={toggleLoginModal} title="登录/注册">
                <UserIcon size={20} />
              </button>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className={`mobile-menu-toggle mobile-only touch-target ${mobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
            aria-expanded={mobileMenuOpen}
          >
            <span className={`hamburger ${mobileMenuOpen ? 'active' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>

        {/* Mobile Sidebar Menu */}
        <div className={`mobile-sidebar ${mobileMenuOpen ? 'open' : ''}`} ref={mobileMenuRef}>
          <div className="mobile-sidebar-content">
            <div className="mobile-sidebar-header">
              {isAuthenticated ? (
                <>
                  <div className="mobile-user-info">
                    {user.avatar ? (
                      <img
                        src={user.avatar.url.startsWith('http')
                          ? user.avatar.url
                          : `${API_URL}${user.avatar.url}`
                        }
                        alt={user.username}
                        className="mobile-user-avatar"
                      />
                    ) : (
                      <div className="mobile-user-avatar-placeholder">
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="mobile-user-details">
                      <span className="mobile-username">{user.username}</span>
                      <button className="mobile-user-settings" onClick={() => {
                        navigate('/profile');
                        setMobileMenuOpen(false);
                      }}>
                        个人信息
                      </button>
                    </div>
                    <button
                      className="mobile-logout-icon touch-target"
                      onClick={handleLogout}
                      title="退出登录"
                    >
                      <LogoutIcon size={18} />
                    </button>
                  </div>
                  <div className="mobile-actions">
                    <div className="mobile-actions-group">
                      <button
                        className="mobile-action-button touch-target"
                        onClick={toggleCreateContentModal}
                      >
                        <PenIcon size={18} />
                        <span>创建内容</span>
                      </button>
                      <button
                        className="mobile-action-button touch-target"
                        onClick={() => {
                          toggleSearchModal();
                          setMobileMenuOpen(false);
                        }}
                      >
                        <SearchIcon size={18} />
                        <span>搜索</span>
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <button className="mobile-login-button touch-target" onClick={toggleLoginModal}>
                  <UserIcon size={16} />
                  <span>登录/注册</span>
                </button>
              )}
            </div>

            <nav className="mobile-nav-menu">
              {globalConfig?.Header?.navitems?.map((item, index) => (
                <NavLink
                  key={index}
                  to={item.herf || '/'}
                  className="mobile-nav-link touch-target"
                  end={item.herf === '/'}
                  onClick={handleMobileNavClick}
                >
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)} />}
      </nav>

      <Modal
        isOpen={loginModalOpen && !isAuthenticated}
        onClose={() => setLoginModalOpen(false)}
        preventOutsideClick={true}
      >
        <LoginModal onClose={() => setLoginModalOpen(false)} />
      </Modal>

      <CreateContentModal 
        isOpen={createContentModalOpen}
        onClose={() => setCreateContentModalOpen(false)}
        onCreateIdea={handleCreateIdea}
        onCreateArticle={handleCreateArticle}
      />

      <Modal
        isOpen={createIdeaModalOpen}
        onClose={() => setCreateIdeaModalOpen(false)}
        width="800px"
        fullHeight={true}
        hideCloseButton={true}
        preventOutsideClick={true}
      >
        <CreateIdeaModal 
          onClose={() => setCreateIdeaModalOpen(false)}
          onSubmit={handleCreateIdeaSubmit}
        />
      </Modal>

      <CreateArticleModal
          isOpen={createArticleModalOpen}
          onClose={toggleCreateArticleModal}
        />

      {/* 搜索模态框 */}
      <SearchModal 
        isOpen={searchModalOpen}
        onClose={() => {
          setSearchModalOpen(false);
          setInitialSearchQuery(''); // 清除初始查询
        }}
        initialQuery={initialSearchQuery}
      />
    </>
  );
};

export default Navbar;